"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get formatColumn () {
        return formatColumn;
    },
    get generateCastQuery () {
        return generateCastQuery;
    }
});
const _nocodbsdk = require("nocodb-sdk");
const _ncError = require("~/helpers/ncError");
const _constants = require("~/db/sql-client/lib/pg/constants");
/*
 * Generate query to extract number from a string. The number is extracted by
 * removing all non-numeric characters from the string. Decimal point is allowed.
 * If there are more than one decimal points, only the first one is considered, the rest are ignored.
 * Negatives are preserved. If statement starts with '-', number will be negated.
 *
 * @param {String} source - source column name
 * @returns {String} - query to extract number from a string
 */ function extractNumberQuery(source) {
    return `
    CAST(
      NULLIF(
        REPLACE(
          REPLACE(
            REPLACE(
              REGEXP_REPLACE(
                REPLACE(
                  REGEXP_REPLACE(
                    REGEXP_REPLACE(${source}, '[^0-9.-]', '', 'g'),
                    '^-', '~'
                  ),
                  '-', ''
                ),
                '(\\d)\\.(\\d)', '\\1-\\2'
              ), 
              '.', ''
            ), 
            '-', '.'
          ),
          '~', '-'
        ), ''
      ) AS DECIMAL
    )
  `;
}
/*
 * Generate query to cast a value to boolean. The boolean value is determined based on the given mappings.
 *
 * @param {String} columnName - Source column name
 * @returns {String} - query to cast value to boolean
 */ function generateBooleanCastQuery(columnName) {
    return `
    CASE
      WHEN LOWER(${columnName}) IN ('checked', 'x', 'yes', 'y', '1', '[x]', '☑', '✅', '✓', '✔', 'enabled', 'on', 'done', 'true') THEN true
      WHEN LOWER(${columnName}) IN ('unchecked', '', 'no', 'n', '0', '[]', '[ ]', 'disabled', 'off', 'false') THEN false
      ELSE null
    END;
  `;
}
/*
 * Generate query to cast a value to date time based on the given date and time formats.
 *
 * @param {String} source - Source column name
 * @param {String} dateFormat - Date format
 * @param {String} timeFormat - Time format
 * @param {String} functionName - Function name to cast value to date time
 * @returns {String} - query to cast value to date time
 */ function generateDateTimeCastQuery(source, dateFormat) {
    if (!(dateFormat in _constants.DATE_FORMATS)) {
        _ncError.NcError.badRequest(`Invalid date format: ${dateFormat}`);
    }
    const timeFormats = dateFormat === 'empty' ? _constants.TIME_FORMATS : [
        ..._constants.TIME_FORMATS,
        [
            '',
            '^$'
        ]
    ];
    const cases = _constants.DATE_FORMATS[dateFormat].map(([format, regex])=>timeFormats.map(([timeFormat, timeRegex])=>{
            // For empty time format (date only), don't add space in format string
            const formatString = timeFormat ? `${format} ${timeFormat}` : format;
            // Combine regex patterns: remove $ from date regex and ^ from time regex
            const combinedRegex = timeFormat ? `${regex.slice(0, -1)}\\s+${timeRegex.slice(1)}` : regex;
            return `WHEN ${source} ~ '${combinedRegex}' THEN to_date_time_safe(${source}, '${formatString}')`;
        }).join('\n'));
    return `CASE 
    ${cases.join('\n')}
    ELSE NULL
   END;`;
}
/*
 * Generate SQL query to extract a number from a string and make out-of-bounds values NULL.
 *
 * @param {String} source - Source column name.
 * @param {Number} minValue - Minimum allowed value.
 * @param {Number} maxValue - Maximum allowed value.
 * @returns {String} - SQL query to extract number and handle out-of-bounds values.
 */ function generateNumberBoundingQuery(source, minValue, maxValue) {
    return `
  NULLIF(
    NULLIF(
      LEAST(
        ${maxValue + 1}, GREATEST(${minValue - 1}, ${source})
      ), ${minValue - 1}
    ), ${maxValue + 1}
  );
`;
}
/*
 * Generate query to cast a value to duration.
 *
 * @param {String} source - Source column name
 * @returns {String} - query to cast value to duration
 */ function generateToDurationQuery(source) {
    return `
    CASE
      WHEN ${source} ~ '^\\d+:\\d{1,2}$' THEN 60 * CAST(SPLIT_PART(${source}, ':', 1) AS INT) + CAST(SPLIT_PART(${source}, ':', 2) AS INT)
      ELSE ${extractNumberQuery(source)}
    END;
  `;
}
function getDateFormat(format) {
    const y = format.indexOf('Y');
    const m = format.indexOf('M');
    const d = format.indexOf('D');
    if (y < m) {
        if (m < d) return 'ymd';
        else if (y < d) return 'ydm';
        else return 'dym';
    } else if (y < d) return 'myd';
    else if (m < d) return 'mdy';
    else return 'dmy';
}
function generateCastQuery(uidt, dt, source, limit, format) {
    switch(uidt){
        case _nocodbsdk.UITypes.SingleLineText:
        case _nocodbsdk.UITypes.MultiSelect:
        case _nocodbsdk.UITypes.SingleSelect:
        case _nocodbsdk.UITypes.Email:
        case _nocodbsdk.UITypes.PhoneNumber:
        case _nocodbsdk.UITypes.URL:
            return `${source}::VARCHAR(${limit || 255});`;
        case _nocodbsdk.UITypes.LongText:
            return `${source}::TEXT;`;
        case _nocodbsdk.UITypes.Number:
            return `CAST(${extractNumberQuery(source)} AS BIGINT);`;
        case _nocodbsdk.UITypes.Year:
            return generateNumberBoundingQuery(extractNumberQuery(source), 1000, 9999);
        case _nocodbsdk.UITypes.Decimal:
        case _nocodbsdk.UITypes.Currency:
            return `${extractNumberQuery(source)};`;
        case _nocodbsdk.UITypes.Percent:
            return `LEAST(100, GREATEST(0, ${extractNumberQuery(source)}));`;
        case _nocodbsdk.UITypes.Rating:
            return `LEAST(${limit || 5}, GREATEST(0, ${extractNumberQuery(source)}));`;
        case _nocodbsdk.UITypes.Checkbox:
            return generateBooleanCastQuery(source);
        case _nocodbsdk.UITypes.Date:
            return `CAST(${generateDateTimeCastQuery(source, getDateFormat(format)).slice(0, -1)} AS DATE);`;
        case _nocodbsdk.UITypes.DateTime:
            return generateDateTimeCastQuery(source, getDateFormat(format));
        case _nocodbsdk.UITypes.Time:
            return generateDateTimeCastQuery(source, 'empty');
        case _nocodbsdk.UITypes.Duration:
            return generateToDurationQuery(source);
        default:
            return `null::${dt};`;
    }
}
function formatColumn(columnName, uiDataType) {
    switch(uiDataType){
        case _nocodbsdk.UITypes.LongText:
        case _nocodbsdk.UITypes.SingleLineText:
        case _nocodbsdk.UITypes.MultiSelect:
        case _nocodbsdk.UITypes.Email:
        case _nocodbsdk.UITypes.URL:
        case _nocodbsdk.UITypes.SingleSelect:
        case _nocodbsdk.UITypes.PhoneNumber:
            return columnName;
        case _nocodbsdk.UITypes.Number:
        case _nocodbsdk.UITypes.Decimal:
        case _nocodbsdk.UITypes.Currency:
        case _nocodbsdk.UITypes.Percent:
        case _nocodbsdk.UITypes.Rating:
        case _nocodbsdk.UITypes.Duration:
        case _nocodbsdk.UITypes.Year:
            return `CAST(${columnName} AS VARCHAR(255))`;
        case _nocodbsdk.UITypes.Checkbox:
            return `CAST(CASE WHEN ${columnName} THEN '1' ELSE '0' END AS TEXT)`;
        default:
            return `CAST(${columnName} AS TEXT)`;
    }
}
